'use client'

import { RelationSchema } from '@backend/schemas'
import { zodResolver } from '@hookform/resolvers/zod'
import i18n from 'i18next'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import z from 'zod'

import { useInviteToIndividual } from '@/api/hooks/useInviteToIndividual'
import PidInput, { fullPidSchema } from '@/components/molecules/pid-input'
import { RelationFieldGroup } from '@/components/molecules/relation-field-group'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field'

const schema = z
  .object({
    consent: z.literal(true, { error: () => i18n.t('formValidation.consentRequired') }),
    email: z.email().or(z.literal('')),
    isGuardian: z.boolean().default(false),
    isLegalRepresentative: z.boolean().default(false),
    pid: fullPidSchema,
    relation: RelationSchema,
    relationOther: z.string().optional(),
  })
  .refine(data => data.relation !== 'other' || !!data.relationOther?.trim(), {
    error: () => i18n.t('formValidation.relationOtherRequired'),
    path: ['relationOther'],
  })

type InviteUserInput = z.input<typeof schema>

type Props = {
  individualId: string
}

export const InviteUserDialog = ({ individualId }: Props) => {
  const [open, setOpen] = useState(false)
  const form = useForm<InviteUserInput>({
    defaultValues: {
      email: '',
      isGuardian: false,
      isLegalRepresentative: false,
      pid: '',
    },
    resolver: zodResolver(schema),
  })
  const relation = form.watch('relation')
  const relationOther = form.watch('relationOther')
  const isGuardian = form.watch('isGuardian') ?? false
  const isLegalRepresentative = form.watch('isLegalRepresentative') ?? false
  const { mutateAsync: inviteOwner } = useInviteToIndividual()
  const { t } = useTranslation()

  const handleSubmit = async (values: InviteUserInput) => {
    if (!individualId) return

    try {
      await inviteOwner({
        individualId,
        requestBody: {
          isGuardian: values.isGuardian ?? false,
          isLegalRepresentative: values.isLegalRepresentative ?? false,
          pid: values.pid,
          relation: values.relation,
          relationOther: values.relation === 'other' ? values.relationOther : undefined,
        },
      })
      form.reset()
      setOpen(false)
    } catch (error) {
      toast.error((error as Error).message)
    }
  }

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <Button>{t('settingsPage.inviteOwnersHeader.inviteButton')}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('inviteUserDialog.title')}</DialogTitle>
          <DialogDescription>{t('inviteUserDialog.description')}</DialogDescription>
        </DialogHeader>

        <FormProvider {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
            <FieldGroup>
              <Field>
                <FieldLabel>{t('defaultFormInputs.pid.label')} *</FieldLabel>
                <PidInput name="pid" />
                <FieldDescription>{t('defaultFormInputs.pid.description')}</FieldDescription>
              </Field>

              <RelationFieldGroup
                idPrefix="invite"
                includeOther
                isGuardian={isGuardian}
                isLegalRepresentative={isLegalRepresentative}
                onIsGuardianChange={value => form.setValue('isGuardian', value)}
                onIsLegalRepresentativeChange={value =>
                  form.setValue('isLegalRepresentative', value)
                }
                onRelationChange={value =>
                  form.setValue('relation', value as z.infer<typeof RelationSchema>, {
                    shouldValidate: true,
                  })
                }
                onRelationOtherChange={value =>
                  form.setValue('relationOther', value, { shouldValidate: true })
                }
                perspective="third-person"
                relation={relation}
                relationDescription={t('relation.inviteDescription')}
                relationError={form.formState.errors.relation?.message}
                relationLabel={`${t('relation.inviteLabel')} *`}
                relationOther={relationOther}
                relationOtherError={form.formState.errors.relationOther?.message}
              />

              <Field>
                <FieldLabel>{t('inviteUserDialog.consentDivider')}</FieldLabel>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <Checkbox
                    checked={form.watch('consent')}
                    onCheckedChange={checked =>
                      form.setValue(
                        'consent',
                        checked === true ? true : (undefined as unknown as true),
                        {
                          shouldValidate: true,
                        },
                      )
                    }
                  />
                  <span className="text-sm leading-snug">{t('inviteUserDialog.consentLabel')}</span>
                </label>
                {form.formState.errors.consent && (
                  <p className="text-error text-sm">{form.formState.errors.consent.message}</p>
                )}
              </Field>

              {/* <Field>
                <FieldLabel>{t('inviteUserDialog.emailLabel')}</FieldLabel>
                <Input
                  autoComplete="off"
                  placeholder={t('inviteUserDialog.emailPlaceholder')}
                  type="email"
                  {...form.register('email')}
                />
                <FieldDescription>{t('inviteUserDialog.emailDescription')}</FieldDescription>
                {form.formState.errors.email && (
                  <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
                )}
              </Field> */}
            </FieldGroup>

            <div className="flex justify-end gap-2">
              <Button
                onClick={() => {
                  setOpen(false)
                  form.reset()
                }}
                type="button"
                variant="outline"
              >
                {t('global.cancel')}
              </Button>
              <Button isLoading={form.formState.isSubmitting} type="submit">
                {t('inviteUserDialog.submitButton')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
